import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { Mail, ShieldCheck, ArrowLeft, Loader2 } from 'lucide-react';
import '../styles/Login.css';

export default function Login() {
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [etapa, setEtapa] = useState(1);
  const [carregando, setCarregando] = useState(false);
  
  const [videos, setVideos] = useState([]);
  const [videoIndex, setVideoIndex] = useState(0);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const playlist = [
      "/assets/video1.mp4",
      "/assets/video2.mp4",
      "/assets/video3.mp4",
      "/assets/video4.mp4"
    ];
    setVideos(playlist);
  }, []);

  const handleVideoEnd = () => {
    if (videos.length > 0) {
      setVideoIndex((prev) => (prev + 1) % videos.length);
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {
        console.log("Autoplay bloqueado ou falhou");
      });
    }
  }, [videoIndex]);

  const handleSolicitarCodigo = async (e) => {
    e.preventDefault();
    setCarregando(true);
    try {
      await api.post('/auth/solicitar-codigo', { email });
      toast.success('Código enviado para seu e-mail!');
      setEtapa(2);
    } catch (err) {
      const msg = err.response?.data?.error || 'E-mail não encontrado no sistema.';
      toast.error(msg);
    } finally {
      setCarregando(false);
    }
  };

  const handleVerificarCodigo = async (e) => {
    e.preventDefault();
    setCarregando(true);
    try {
      const response = await api.post('/auth/verificar-codigo', { email, code: codigo });
      const { token, user } = response.data;

      if (!user) {
        throw new Error("Dados do usuário não retornados pelo servidor.");
      }

      // Limpa dados antigos da sessão antes de salvar os novos
      localStorage.clear();

      localStorage.setItem('token', token);
      
      // ✅ Captura o cargo/nível de acesso de forma segura independentemente da variação da chave
      const finalRole = user.userRole || user.role || user.nivel_acesso || 'admin';
      localStorage.setItem('userRole', finalRole);
      localStorage.setItem('userName', user.nome || '');
      
      const estabId = user.estabelecimentoId || user.estab || user.estabelecimento_id;
      if (estabId) {
        localStorage.setItem('estabelecimentoId', estabId);
      }

      toast.success("Acesso autorizado!");

      setTimeout(() => {
        // ✅ Direcionamento exato: Se for admin_master ou admin sem ID de estabelecimento específico, vai para o /dashboard
        if (finalRole === "admin_master" || !estabId) {
          navigate("/dashboard", { replace: true });
        } else {
          navigate("/minha-loja", { replace: true });
        }
      }, 500);

    } catch (err) {
      console.error("Erro no login:", err);
      toast.error(err.response?.data?.error || "Código inválido ou erro no login.");
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="login-container">
      {videos.length > 0 && (
        <video
          key={videos[videoIndex]}
          ref={videoRef}
          autoPlay
          muted
          playsInline
          onEnded={handleVideoEnd}
          className="background-video"
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: -1
          }}
        >
          <source src={videos[videoIndex]} type="video/mp4" />
        </video>
      )}

      <div className="video-overlay"></div>
      <Toaster position="top-center" />

      <div className="login-card animate-fade-in">
        <div className="login-header">
          <div className="brand-logo">
            <ShieldCheck size={40} color="#EA1D2C" />
          </div>
          <h1 className="brand-name">UNIVERSO <span>MÍDIA</span></h1>
          <p className="brand-tagline">Painel Administrativo</p>
        </div>

        {etapa === 1 ? (
          <form onSubmit={handleSolicitarCodigo} className="login-form">
            <div className="instruction">
              <h2>Entrada Segura</h2>
              <p>Inicie sua sessão via e-mail.</p>
            </div>
            <div className="input-field">
              <Mail size={18} className="input-icon" />
              <input
                type="email"
                placeholder="Seu e-mail cadastrado..."
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn-primary" disabled={carregando}>
              {carregando ? <Loader2 className="spin" size={20} /> : 'Enviar Código'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerificarCodigo} className="login-form">
            <div className="instruction">
              <h2>Validar Acesso</h2>
              <p>Código enviado para <strong>{email}</strong></p>
            </div>
            <div className="input-field">
              <ShieldCheck size={18} className="input-icon" />
              <input
                type="text"
                maxLength="6"
                className="otp-input"
                placeholder="000000"
                value={codigo}
                onChange={e => setCodigo(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn-primary" disabled={carregando}>
              {carregando ? <Loader2 className="spin" size={20} /> : 'Entrar'}
            </button>
            <button type="button" onClick={() => setEtapa(1)} className="btn-link">
              <ArrowLeft size={14} /> Alterar e-mail
            </button>
          </form>
        )}
      </div>
    </div>
  );
}